import React, { useCallback, useEffect, useState } from 'react';
import PageTitle from '../../components/PageTitle';
import { useIntl } from 'react-intl';
import LoginFormV2 from './components/LoginFormV2';
import SocialLoginForm from '../../components/SocialLoginForm';
import Whitebox from '../../components/WhiteboxPage';
import { LoginPageMessages } from './messages';
import { useMicrobranding } from '../../utilities/hooks/useMicrobranding';
import Footer from './components/Footer';
import Header from './components/Header';
import MaybeSocialLoginPanel from './components/MaybeSocialLoginPanel';
import WhiteboxFooter from './components/WhiteboxFooter';
import { loginPageId } from './constants';
import { addBreadcrumb } from '../../utilities/analytics/error-reporting';
import { isFedM } from '../../utilities/env';
import { useCobrandingApplication, useEmailFromSearchParams } from './hooks/utility';
import { useBaseLoginV2Model } from './hooks/useBaseLoginV2Model';
import { useLoginSubmission } from './hooks/useLoginSubmission';
import { useSocialLogin } from './hooks/useSocialLogin';
import { useGoogleOneTap } from './hooks/useGoogleOneTap';
import RememberedAccountsPage from './remembered-accounts/page';
import MaybePasskeyLoginPanel from './components/Passkey';
import { usePasskeyLogin } from './hooks/usePasskeyLogin';
import { useOrgLoginConfig } from './hooks/useOrgLoginConfig';

const LoginPageV2 = () => {
  const { oidcContext, isEmbedded, isMobileApp, screenSize } = useMicrobranding();

  const {
    isPasswordlessAllowed,
    checkUsername,
    isUsernameChecked,
    canSubmitPassword,
    checkUsernameUserActions,
    selectedCheckUsernameUserAction,
    onSelectCheckUsernameUserAction,
    onContinueCheckUsernameUserAction,
    checkUsernameError,
    error: loginError,
    redirectType,
    isRestrictedEmail,
    isCheckUsernameLoading,
    resetLogin,
    infoCode,
    rememberMeEnabled,
    isRememberMeChecked,
    onToggleRememberMeCheckbox,
    onValidationError,
  } = useBaseLoginV2Model();

  const {
    isLoading,
    onLogin: handleLogIn,
    onPasswordlessLogin: handlePasswordlessLogIn,
  } = useLoginSubmission({ loginStateSource: 'v2' });

  const {
    onAppleLogin,
    onSlackLogin,
    onMicrosoftLogin,
    onGoogleSocialLogin,
    onDismissGoogleOneTap,
    socialLoginEligibility,
    envSupportsSocialLogin,
    isSocialBlocked,
  } = useSocialLogin({ loginStateSource: 'v2' });

  const { onPasskeyLogin, authenticating } = usePasskeyLogin();

  const { redirectToSingleSignOn } = useOrgLoginConfig();

  const cobrandingApplication = useCobrandingApplication();

  const isLoginSecondStep = isUsernameChecked;

  const { formatMessage } = useIntl();

  const emailFromSearchParams = useEmailFromSearchParams();

  const [email, setEmail] = useState(() => emailFromSearchParams || '');

  useEffect(() => {
    if (emailFromSearchParams) {
      setEmail(emailFromSearchParams);
    }
  }, [emailFromSearchParams]);

  useGoogleOneTap({ email });

  const { highlightedSocialProvider = null, isEligibleForSocialLoginOnly = null } =
    socialLoginEligibility || {};

  const showSocialLoginOnly =
    isLoginSecondStep &&
    isEligibleForSocialLoginOnly &&
    highlightedSocialProvider &&
    envSupportsSocialLogin;

  const handleGoogleSocialLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onGoogleSocialLogin(email);
    }
  }, [email, onGoogleSocialLogin, envSupportsSocialLogin]);

  const handleMicrosoftLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onMicrosoftLogin(email);
    }
  }, [email, onMicrosoftLogin, envSupportsSocialLogin]);

  const handleAppleLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onAppleLogin(email);
    }
  }, [email, onAppleLogin, envSupportsSocialLogin]);

  const handleSlackLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onSlackLogin(email);
    }
  }, [email, onSlackLogin, envSupportsSocialLogin]);

  const handlePasskeyLogin = useCallback(() => {
    onPasskeyLogin();
  }, [onPasskeyLogin]);

  const handleContinue = (email: string) => {
    checkUsername(email);
  };

  const handleFormClick = useCallback(() => {
    if (envSupportsSocialLogin && screenSize === 'mobile') {
      onDismissGoogleOneTap();
    }
  }, [onDismissGoogleOneTap, screenSize, envSupportsSocialLogin]);

  const handleEmailChange = useCallback((email: string) => {
    setEmail(email);
    if (isFedM()) {
      addBreadcrumb({
        level: 'debug',
        message: 'FedRAMP login email input change',
        category: 'ui',
        data: {
          emailLength: email.length,
          emailType: typeof email,
          emailConstructor: email && email.constructor ? email.constructor.name : undefined,
        },
      });
    }
  }, []);

  return (
    <>
      <PageTitle title={formatMessage(LoginPageMessages.headTitle)} />
      <Whitebox
        pageId={loginPageId}
        header={
          <Header
            highlightedSocialProvider={highlightedSocialProvider}
            showSocialLoginOnly={showSocialLoginOnly}
          />
        }
        footer={<Footer oidcContext={oidcContext} isEmbedded={isEmbedded} />}
        whiteboxFooter={
          <WhiteboxFooter
            onUseDifferentLoginMethod={resetLogin}
            showSocialLoginOnly={showSocialLoginOnly}
            isMobileApp={isMobileApp}
            oidcContext={oidcContext}
          />
        }>
        {showSocialLoginOnly ? (
          <SocialLoginForm
            email={email}
            highlightedSocialProvider={highlightedSocialProvider}
            handleGoogleLogin={handleGoogleSocialLogin}
            handleMicrosoftLogin={handleMicrosoftLogin}
            handleAppleLogin={handleAppleLogin}
            handleSlackLogin={handleSlackLogin}
          />
        ) : (
          <>
            <LoginFormV2
              application={cobrandingApplication || null}
              redirectType={!isLoginSecondStep ? redirectType : null}
              isRedirecting={redirectType !== null}
              isSecondStep={isLoginSecondStep}
              canSubmitPassword={canSubmitPassword}
              checkUsernameUserActions={checkUsernameUserActions}
              selectedCheckUsernameUserAction={selectedCheckUsernameUserAction}
              onSelectCheckUsernameUserAction={onSelectCheckUsernameUserAction}
              onContinueCheckUsernameUserAction={onContinueCheckUsernameUserAction}
              isPasswordlessAllowed={isPasswordlessAllowed}
              isLoading={isLoading}
              isCheckUsernameLoading={isCheckUsernameLoading}
              onContinue={handleContinue}
              onLogIn={handleLogIn}
              onPasswordlessLogIn={handlePasswordlessLogIn}
              error={loginError}
              info={infoCode}
              fieldErrors={{
                email: checkUsernameError,
              }}
              email={email}
              onValidationError={onValidationError}
              onEditEmail={resetLogin}
              onFormClick={handleFormClick}
              restrictedEmail={isRestrictedEmail}
              pageId={loginPageId}
              onEmailChange={handleEmailChange}
              showRememberMeCheckbox={rememberMeEnabled}
              rememberMeChecked={isRememberMeChecked}
              onToggleRememberMeCheckbox={onToggleRememberMeCheckbox}
              onRedirectToSingleSignOn={redirectToSingleSignOn}
            />

            <MaybePasskeyLoginPanel
              onPasskeyLogin={handlePasskeyLogin}
              authenticating={authenticating}
            />

            <MaybeSocialLoginPanel
              isEmbedded={isEmbedded}
              envSupportsSocialLogin={envSupportsSocialLogin}
              isSocialBlocked={isSocialBlocked}
              onSlackLogin={handleSlackLogin}
              onAppleLogin={handleAppleLogin}
              onMicrosoftLogin={handleMicrosoftLogin}
              onGoogleLogin={handleGoogleSocialLogin}
            />
          </>
        )}
      </Whitebox>
    </>
  );
};

function BasePageV2() {
  // TODO: KIRBY-8427: tidy up this double check.
  const { shouldShowRememberedAccounts } = useBaseLoginV2Model({ autoCheckUsername: false });
  if (shouldShowRememberedAccounts) {
    return <RememberedAccountsPage loginStateSource="v2" />;
  }
  return <LoginPageV2 />;
}

export default BasePageV2;
